import { Leaf, TrendingUp, Sun, Droplets, ShieldCheck, FlaskConical, Lightbulb, Eye } from "lucide-react";
import { PlantingCalendar } from "@/components/PlantingCalendar";

const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("excellent")) return "text-emerald-600 bg-emerald-50 border-emerald-200";
  if (s.includes("good")) return "text-lime-600 bg-lime-50 border-lime-200";
  if (s.includes("fair")) return "text-amber-600 bg-amber-50 border-amber-200";
  return "text-red-600 bg-red-50 border-red-200";
};

const profitColor = (p) => {
  const s = (p || "").toLowerCase();
  if (s === "high") return "bg-emerald-100 text-emerald-700";
  if (s === "medium") return "bg-amber-100 text-amber-700";
  return "bg-stone-100 text-stone-600";
};

export function ResultsView({ analysis }) {
  if (!analysis) return null;
  const health = analysis.soil_health || {};
  const crops = analysis.crops || [];
  const fertilizers = analysis.fertilizers || [];
  const treatments = analysis.treatments || [];
  const tips = analysis.tips || [];
  const score = Math.round(health.score || 0);

  return (
    <div className="space-y-8" data-testid="results-view">
      {/* Soil health */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="relative h-28 w-28 shrink-0">
            <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#E7E5E4" strokeWidth="10" />
              <circle cx="50" cy="50" r="42" fill="none" stroke="#2D6A4F" strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${(score / 100) * 264} 264`} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono-tech font-bold text-2xl text-emerald-950" data-testid="soil-score">{score}</span>
              <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">/ 100</span>
            </div>
          </div>
          <div>
            <div className={`inline-block text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full border mb-2 ${statusColor(health.status)}`}>
              {health.status || "Unknown"}
            </div>
            <p className="text-stone-700 leading-relaxed">{health.summary}</p>
          </div>
        </div>
        {analysis.photo_observations && (
          <div data-testid="results-photo-observations" className="mt-6 bg-[#EAF6EF] rounded-xl p-4 flex gap-3">
            <Eye className="h-5 w-5 text-[#2D6A4F] shrink-0 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">Seen in your photo · {analysis.confidence || "—"} confidence</div>
              <p className="text-sm text-stone-700 leading-relaxed">{analysis.photo_observations}</p>
            </div>
          </div>
        )}
      </div>

      {/* Crops */}
      {crops.length > 0 && (
        <div>
          <h3 className="font-display text-xl font-semibold text-emerald-900 mb-4 flex items-center gap-2"><Leaf className="h-5 w-5" /> Recommended Crops</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {crops.map((c, i) => (
              <div key={i} data-testid={`crop-card-${i}`} className="bg-white rounded-2xl border border-stone-200 p-5 hover:border-[#52B788] transition-colors">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h4 className="font-display text-lg font-semibold text-emerald-950">{c.name}</h4>
                  <span className="font-mono-tech text-sm font-bold px-2.5 py-1 rounded-lg bg-[#EAF6EF] text-[#2D6A4F] whitespace-nowrap">{Math.round(c.match || 0)}% match</span>
                </div>
                <p className="text-sm text-stone-600 leading-relaxed mb-4">{c.why}</p>
                <div className="flex flex-wrap gap-3 text-xs text-stone-600">
                  <span className="inline-flex items-center gap-1"><Sun className="h-3.5 w-3.5 text-amber-500" />{c.sunlight}</span>
                  <span className="inline-flex items-center gap-1"><Droplets className="h-3.5 w-3.5 text-blue-500" />{c.water}</span>
                  <span className="inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-emerald-500" />{c.yield}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${profitColor(c.profit)}`}>{c.profit} profit</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <PlantingCalendar crops={crops} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fertilizers */}
        {fertilizers.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2"><FlaskConical className="h-5 w-5 text-[#D97706]" /> Fertilisers & Products</h3>
            <ul className="space-y-3">
              {fertilizers.map((f, i) => (
                <li key={i} data-testid={`fertilizer-${i}`} className="border-l-2 border-[#D97706] pl-3">
                  <div className="font-semibold text-emerald-950">{f.name}</div>
                  <div className="text-sm text-stone-600">{f.purpose}</div>
                  <div className="text-xs font-mono-tech text-[#78350F] mt-0.5">{f.dosage}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Treatments */}
        {treatments.length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#2D6A4F]" /> Treatment Plan</h3>
            <ol className="space-y-3">
              {treatments.map((t, i) => (
                <li key={i} data-testid={`treatment-${i}`} className="flex gap-3">
                  <span className="shrink-0 h-6 w-6 rounded-full bg-[#EAF6EF] text-[#2D6A4F] font-mono-tech text-sm font-bold flex items-center justify-center">{i + 1}</span>
                  <div>
                    <div className="font-semibold text-emerald-950 text-sm">{t.step}</div>
                    <div className="text-sm text-stone-600">{t.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Tips */}
      {tips.length > 0 && (
        <div className="bg-[#143D29] text-emerald-50 rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Lightbulb className="h-5 w-5 text-[#52B788]" /> Farm Tips</h3>
          <ul className="grid sm:grid-cols-2 gap-3">
            {tips.map((t, i) => (
              <li key={i} data-testid={`tip-${i}`} className="flex gap-2 text-sm text-emerald-100/90">
                <span className="text-[#52B788] mt-0.5">•</span>{t}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
